import { Form, Space, Select, TreeSelect, Button, Card, Tag } from 'antd'; // 请确保正确导入所需的组件
import React, { useState, useEffect, useMemo } from 'react';
import { FieldDataNode } from 'rc-tree/lib/interface';
import { SNLLibrary } from '../../../models/snl/SNLLibrary';
import { FormInstance } from 'antd/lib/form/Form';
import { RuleTreeDataNode } from '../../../utils/snlUtils';
import {
  selectSNLs
} from '../tuzhiVisSlice';
import { useAppSelector } from '../../../app/hooks';
import SNLService from './SNL.service';
import { CloseOutlined } from '@ant-design/icons';

interface OnlineSNLFormProps {
  form: FormInstance
}
interface SNLFormValue {
  snlFile?: File;
  selectedRules?: string[];
  selectedTags?: string[];
  selectedSnlTags?: string[];
  snlFileData?: string;
  tagBoolExpr?: string[]; 
}

interface SNLComponentProps {
  id?: string;
  value?: SNLFormValue;
  onChange?: (value: SNLFormValue) => void;
}
const SNLComponet: React.FC<SNLComponentProps> = (componentProps) => {
    const { id, value = {}, onChange } = componentProps;
    const [onlineSnlFile, setOnlineSnlFile] = useState<File | null>(null); // SNL文件内容
    const [onlineSnlLibrary, setOnlineSnlLibrary] = useState<SNLLibrary | null>(null); // SNL库对象
  
    const [onlineSelectedRules, setOnlineSelectedRules] = useState<string[]>([]); // SNL选择的条文编号列表
    const [onlineSnlFileData, setOnlineSnlFileData] = useState<string>(''); // snl文件内容
    const [initialAdd, setInitialAdd] = useState(true);
    const snls = useAppSelector(selectSNLs);
    const [tagTokens, setTagTokens] = useState<string[]>([]);
    const [availableSnlTags, setAvailableSnlTags] = useState<{ value: string; label: string }[]>([]);
    const [tagGroups, setTagGroups] = useState<any[]>([]);

useEffect(() => {
  if (initialAdd) {
    setInitialAdd(false);
    return;  // 第一次只标记，不触发 onChange
  }
  if (onChange) {
    onChange({
      selectedRules: onlineSelectedRules,
      snlFile: onlineSnlFile ?? undefined,
      snlFileData: onlineSnlFileData,
      tagBoolExpr: tagTokens,
    });
  }
}, [onlineSelectedRules, onlineSnlFile, tagTokens]);

  const evaluateTagBoolExpr = (tokens: string[], tags: string[]): boolean => {
    const pos = { i: 0 };
    const parseOR = (): boolean => {
      let v = parseAND();
      while (pos.i < tokens.length && tokens[pos.i] === 'OR') { pos.i++; v = v || parseAND(); }
      return v;
    };
    const parseAND = (): boolean => {
      let v = parseNOT();
      while (pos.i < tokens.length && tokens[pos.i] === 'AND') { pos.i++; v = v && parseNOT(); }
      return v;
    };
    const parseNOT = (): boolean => {
      if (tokens[pos.i] === 'NOT') { pos.i++; return !parsePrimary(); }
      return parsePrimary();
    };
    const parsePrimary = (): boolean => {
      if (tokens[pos.i] === '(') {
        pos.i++;
        const v = parseOR();
        if (tokens[pos.i] === ')') pos.i++;
        return v;
      }
      return tags.includes(tokens[pos.i++]);
    };
    return parseOR();
  };
const isCompleteExpr = (toks: string[]) => {
  if (toks.length === 0) return false;
  const last = toks[toks.length - 1];
  const afterTag = !['AND', 'OR', 'NOT', '('].includes(last);
  const openP = toks.reduce((n, t) => n + (t === '(' ? 1 : t === ')' ? -1 : 0), 0);
  return afterTag && openP === 0;
};
const onlineRuleTreeData = useMemo(() => {
  if (!onlineSnlLibrary) return [];
  const tree = onlineSnlLibrary.getAntdTree([], []);
  // 表达式为空或未完成时不过滤
  if (!tagTokens || tagTokens.length === 0 || !isCompleteExpr(tagTokens)) return [tree];
  const applyDisabled = (node: any): any => {
    if (!node.children || node.children.length === 0) {
      const rule = Object.values(onlineSnlLibrary.rules).find(r => r.number === node.ruleNumber);
      const hasMatch = rule?.snls.some(snl => {
        const snlTags = snl.tags.map(t => t.id);
        return evaluateTagBoolExpr(tagTokens, snlTags);
      });
      return { ...node, disabled: !hasMatch };
    }
    return { ...node, children: node.children.map(applyDisabled) };
  };
  return [applyDisabled(tree)];
}, [onlineSnlLibrary, tagTokens]);

    return (
      <Space.Compact direction="vertical" style={{ width: '100%' }}>
        <Select
          style={{ width: '100%' }}
          showSearch
          placeholder="在线SNL"
          optionFilterProp="children"
          onSelect={(v: string) => {
            setOnlineSelectedRules([]);
            SNLService.getOnlineSNLContent(v)
              .then(res => {
                setOnlineSnlFile(new File([res.data], v)); // 构造File对象兼容本地上传内容
                setOnlineSnlFileData(res.data);
                const snlLibrary = SNLLibrary.loadFromXMLString(res.data);
                const tags = snlLibrary.getAntdSnlTagsTree().map((node: any) => ({
                  value: node.value,  // id，用于过滤
                  label: node.title,  // name，用于显示
                }));
                setOnlineSnlLibrary(snlLibrary);
                setAvailableSnlTags(tags);
                const tagTree = snlLibrary.getAntdTagsTree();
                setTagGroups(tagTree)
              });
          }}
          filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
          options={snls.snls.map(name => ({
            label: name, value: name,
          }))}
        />
        <TagBoolBuilder
          tokens={tagTokens}
          onChange={(newTokens) => {
            setTagTokens(newTokens);
            if (isCompleteExpr(newTokens)) {
              setOnlineSelectedRules([]);
            }
          }}
          availableTags={availableSnlTags}
          tagGroups={tagGroups}
        />
        <TreeSelect
          style={{
            width: '100%',
          }}
          treeData={onlineRuleTreeData}
          value={onlineSelectedRules}
          onChange={(newValue) => {
            setOnlineSelectedRules(newValue);
          }}
          treeCheckable={true}
          placeholder="选择审查规范编号"
          treeLine
          autoClearSearchValue
          treeNodeFilterProp="title"
          treeDefaultExpandAll
          showCheckedStrategy={TreeSelect.SHOW_CHILD}
          maxTagCount={0}
          maxTagPlaceholder={(v) => <span>{`已选择${v.length}条`}</span>}
          listHeight={400}
        />
      </Space.Compact>
    );
  }


export type TagBoolBuilderProps = {
  tokens: string[];
  onChange: (tokens: string[]) => void;
  availableTags: { value: string; label: string }[];
  tagGroups: any[];
};

export const TagBoolBuilder: React.FC<TagBoolBuilderProps> = ({ tokens, onChange, availableTags, tagGroups }) => {

  const getState = (toks: string[]) => {
    if (toks.length === 0) return 'START';
    const last = toks[toks.length - 1];
    if (last === 'AND' || last === 'OR') return 'AFTER_BIN';
    if (last === 'NOT') return 'AFTER_NOT';
    if (last === '(') return 'AFTER_LP';
    if (last === ')') return 'AFTER_RP';
    return 'AFTER_TAG';
  };

  const openParens = (toks: string[]) =>
    toks.reduce((n, t) => n + (t === '(' ? 1 : t === ')' ? -1 : 0), 0);

  const canAdd = (item: string, toks: string[]): boolean => {
    const st = getState(toks);
    const isTag = availableTags.some(t => t.value === item);

    if (isTag) return ['START', 'AFTER_BIN', 'AFTER_NOT', 'AFTER_LP'].includes(st);
    if (item === 'AND' || item === 'OR') return ['AFTER_TAG', 'AFTER_RP'].includes(st);
    if (item === 'NOT') return ['START', 'AFTER_BIN', 'AFTER_LP'].includes(st);
    if (item === '(') return ['START', 'AFTER_BIN', 'AFTER_NOT', 'AFTER_LP'].includes(st);
    if (item === ')') return ['AFTER_TAG', 'AFTER_RP'].includes(st) && openParens(toks) > 0;
    return false;
  };

  const isComplete = (toks: string[]) => {
    if (toks.length === 0) return false;
    return ['AFTER_TAG', 'AFTER_RP'].includes(getState(toks)) && openParens(toks) === 0;
  };


const groups = useMemo(() => {
  if (availableTags.length === 0) return [];
  if (tagGroups.length === 0) return [{ label: '标签', tags: availableTags }];

  const valueSet = new Set(availableTags.map(t => t.value));
  const titleOf = (id: string) => tagGroups.find(g => g.value === id)?.title ?? id;
  const childrenOf = (id: string | null) =>
    tagGroups.filter(g => (g.parentId ?? null) === id);

  const collectLeaves = (id: string): { value: string; label: string }[] => {
    const kids = childrenOf(id);
    if (kids.length === 0) {
       return [{ value: id, label: titleOf(id) }];
    }
    return kids.flatMap(k => collectLeaves(k.value));
  };

  const roots = childrenOf(null).filter(r => r.value !== 'no-tag');
  return roots
    .map(root => ({ label: titleOf(root.value), tags: collectLeaves(root.value) }))
    .filter(g => g.tags.length > 0);
}, [tagGroups, availableTags]);

  const complete = isComplete(tokens);
  const displayExpr = tokens.map(t => {
  const found = availableTags.find(tag => tag.value === t);
  return found ? found.label : t;
}).join(' ');

  return (
    <Space direction="vertical" size={4} style={{ width: '100%' }}>

      {/* 表达式预览 */}
      <div style={{
        background: '#fafafa',
        border: `1px solid ${complete ? '#52c41a' : tokens.length > 0 ? '#ff4d4f' : '#d9d9d9'}`,
        borderRadius: 6,
        padding: '4px 8px',
        minHeight: 32,
        fontSize: 12,
        fontFamily: 'monospace',
        wordBreak: 'break-all',
        color: '#333',
      }}>
        {tokens.length > 0
          ? displayExpr
          : <span style={{ color: '#bbb' }}>点击下方标签构造过滤表达式（留空则不过滤）</span>
        }
      </div>

      {/* 语法提示 */}
      {tokens.length > 0 && (
        <div style={{ fontSize: 11, color: complete ? '#52c41a' : '#ff4d4f' }}>
          {complete ? '✓ 表达式合法' : openParens(tokens) > 0 ? `✗ 还有 ${openParens(tokens)} 个括号未关闭` : '✗ 表达式未完成'}
        </div>
      )}
      {groups.map(group => (
  <div key={group.label} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
    <span style={{ 
      fontSize: 11, 
      color: '#888', 
      width: 56,
      flexShrink: 0,
      paddingTop: 2,
      textAlign: 'right',
    }}>
      {group.label}
    </span>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, flex: 1 }}>
      {group.tags.map(tag => (
        <Tag
          key={tag.value}
          color="blue"
          style={{
            cursor: canAdd(tag.value, tokens) ? 'pointer' : 'not-allowed',
            opacity: canAdd(tag.value, tokens) ? 1 : 0.35,
            userSelect: 'none',
            margin: 0,
          }}
          onClick={() => canAdd(tag.value, tokens) && onChange([...tokens, tag.value])}
        >
          {tag.label}
        </Tag>
      ))}
    </div>
  </div>
))}
      {/* 动态标签分组 */}
      

      {/* 算子行 */}
      <Space size={4} wrap>
        {(['AND', 'OR', 'NOT', '(', ')'] as const).map(op => (
          <Tag
            key={op}
            color={
              op === 'AND' ? 'green' :
              op === 'OR'  ? 'gold'  :
              op === 'NOT' ? 'red'   : 'default'
            }
            style={{
              cursor: canAdd(op, tokens) ? 'pointer' : 'not-allowed',
              opacity: canAdd(op, tokens) ? 1 : 0.35,
              fontWeight: 600,
              userSelect: 'none',
              fontFamily: 'monospace',
              marginBottom: 2,
            }}
            onClick={() => canAdd(op, tokens) && onChange([...tokens, op])}
          >
            {op}
          </Tag>
        ))}
        <Tag
          style={{
            cursor: tokens.length > 0 ? 'pointer' : 'not-allowed',
            opacity: tokens.length > 0 ? 1 : 0.35,
            userSelect: 'none',
          }}
          onClick={() => tokens.length > 0 && onChange(tokens.slice(0, -1))}
        >
          ← 删除
        </Tag>
        <Tag
          color="default"
          style={{
            cursor: tokens.length > 0 ? 'pointer' : 'not-allowed',
            opacity: tokens.length > 0 ? 1 : 0.35,
            userSelect: 'none',
          }}
          onClick={() => tokens.length > 0 && onChange([])}
        >
          清空
        </Tag>
      </Space>

    </Space>
  );
};
function OnlineSNLForm(props: OnlineSNLFormProps) {
  const { form } = props;
  const checkOnlineSnl = (_: any, value: SNLFormValue) => {
    if (!value || !value.snlFile) {
      return Promise.reject(new Error('未选择待审查的SNL文件'));
    }

    const snlList = form.getFieldValue('snlList') || [];
    const snlFiles: File[] = snlList.map((s: any) => {
      return s?.componentData?.snlFile;
    });

    const isDuplicate = snlFiles.filter((file: File) => file.name === value.snlFile!.name).length > 1;
    if (isDuplicate) {
      return Promise.reject(new Error('选择的 SNL 文件与之前的文件重复'));
    }

    if (!value.selectedRules || value.selectedRules.length === 0) {
      return Promise.reject(new Error('未选择待审查的条款'));
    }
    return Promise.resolve();
  };



  return (
    <Form
      form={form}
      name="onlineSnlComponents"
      onFinish={(values) => {
        console.log('Form values:', values);
      }}
      initialValues={{ snlList: [{}] }}
    >
      <Form.List name="snlList">
        {(fields, { add, remove }) => (
          <>
            {fields.map((field, index) => (
              <div key={field.key}>
                <Card
                  key={field.key}
                  extra={
                    index > 0 && (
                      <CloseOutlined
                        onClick={() => {
                          remove(field.name);
                        }}
                      />
                    )
                  }
                  style={{ width: '100%' }}
                  size="small"
                >
                  <Form.Item
                    {...field}
                    name={[field.name, 'componentData']}
                    key={field.key}
                    style={{ marginBottom: '0px' }}
                    rules={[{ validator: checkOnlineSnl }]}
                    validateTrigger={["onSubmit"]}
                  >
                    <SNLComponet />
                  </Form.Item>
                </Card>
              </div>
            ))}
            <Button type="dashed" onClick={() => { add(); console.log(form.getFieldsValue()) }} block>
              + 添加规范
            </Button>
          </>
        )}
      </Form.List>
    </Form>
  );
}




export default React.memo(OnlineSNLForm);
